(() => {
  const configElement = document.getElementById('OurbellaAnalyticsConfig');
  if (!configElement) return;

  let config;
  try {
    config = JSON.parse(configElement.textContent);
  } catch (error) {
    console.error('[OURBELLA analytics] Invalid configuration.', error);
    return;
  }

  if (config.design_mode) return;

  const debug = new URLSearchParams(window.location.search).get('ob_analytics_debug') === '1';
  const themeId = String(config.theme_id || 'unknown');
  const isCollabs = config.template_name === 'page' && config.template_suffix === 'collabs';
  const moduleElements = [];
  const inPageViews = new Set();

  const cleanObject = (value) => {
    const output = {};
    Object.entries(value || {}).forEach(([key, item]) => {
      if (item !== undefined && item !== null && item !== '') output[key] = item;
    });
    return output;
  };

  const emit = (eventName, eventData) => {
    const payload = cleanObject({
      theme_id: themeId,
      page_type: config.page_type,
      page_path: window.location.pathname,
      ...eventData,
    });

    try {
      if (window.Shopify?.analytics?.publish) {
        window.Shopify.analytics.publish(`ourbella_${eventName}`, payload);
      }
    } catch (error) {
      if (debug) console.warn('[OURBELLA analytics] Shopify publish failed.', error);
    }

    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', eventName, payload);
      }
    } catch (error) {
      if (debug) console.warn('[OURBELLA analytics] Google publish failed.', error);
    }

    if (debug) console.info(`[OURBELLA analytics] ${eventName}`, payload);
  };

  const eventBase = (moduleId, elementId, clickTarget) => ({
    module_id: moduleId,
    element_id: elementId,
    click_target: clickTarget,
  });

  const findSection = (sectionKey) => {
    return (
      document.getElementById(`shopify-section-${sectionKey}`) ||
      document.querySelector(`[id^="shopify-section-"][id$="__${sectionKey}"]`)
    );
  };

  Object.entries(config.modules || {}).forEach(([sectionKey, moduleId]) => {
    const section = findSection(sectionKey);
    if (!section) return;
    section.dataset.obModuleId = moduleId;
    section.dataset.obSectionKey = sectionKey;
    moduleElements.push(section);
  });

  const sessionViewKey = (moduleId) => `ourbella:module-view:${themeId}:${moduleId}`;

  const hasViewedModule = (moduleId) => {
    if (inPageViews.has(moduleId)) return true;
    try {
      return window.sessionStorage.getItem(sessionViewKey(moduleId)) === '1';
    } catch (_error) {
      return false;
    }
  };

  const rememberModuleView = (moduleId) => {
    inPageViews.add(moduleId);
    try {
      window.sessionStorage.setItem(sessionViewKey(moduleId), '1');
    } catch (_error) {
      // The in-memory set still prevents duplicate views on the current page.
    }
  };

  const startModuleObserver = () => {
    if (!moduleElements.length || !('IntersectionObserver' in window)) return;

    const timers = new Map();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const moduleId = entry.target.dataset.obModuleId;
          const isVisible = entry.isIntersecting && entry.intersectionRatio >= 0.5;

          if (!isVisible) {
            window.clearTimeout(timers.get(entry.target));
            timers.delete(entry.target);
            return;
          }

          if (hasViewedModule(moduleId) || timers.has(entry.target)) return;

          const timer = window.setTimeout(() => {
            if (hasViewedModule(moduleId)) return;
            rememberModuleView(moduleId);
            emit(isCollabs ? 'collabs_module_view' : 'home_module_view', {
              ...eventBase(moduleId, 'module', 'view'),
              section_key: entry.target.dataset.obSectionKey,
            });
            observer.unobserve(entry.target);
            timers.delete(entry.target);
          }, 1000);

          timers.set(entry.target, timer);
        });
      },
      { threshold: [0, 0.5, 1] }
    );

    moduleElements.forEach((section) => observer.observe(section));
  };

  const numberValue = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : undefined;
  };

  const productFromElement = (target, moduleId) => {
    const card = target.closest('[data-ob-product-card]');
    if (!card) return null;

    const currentVariant = card.querySelector('input[name="id"]')?.value || card.dataset.obVariantId;
    return cleanObject({
      item_id: String(card.dataset.obProductId || ''),
      item_name: card.dataset.obProductTitle,
      item_brand: card.dataset.obProductVendor,
      item_handle: card.dataset.obProductHandle,
      item_variant: card.dataset.obVariantTitle,
      variant_id: String(currentVariant || ''),
      price: numberValue(card.dataset.obProductPrice),
      currency: card.dataset.obCurrency,
      index: numberValue(card.dataset.obProductPosition),
      item_list_id: moduleId,
      item_list_name: moduleId,
    });
  };

  const productClickTarget = (target) => {
    if (target.closest('.product-card__quickview')) return 'quick_view';
    if (target.closest('.product-card__atc, .pcard__atc')) {
      const control = target.closest('.product-card__atc, .pcard__atc');
      return control?.getAttribute('aria-haspopup') === 'dialog' || control?.tagName === 'A'
        ? 'choose_options'
        : 'add_to_cart';
    }
    if (target.closest('.testimonial__product')) return 'testimonial_product';
    if (target.closest('.product-card__title, .pcard__title')) return 'product_title';
    if (target.closest('.product-card__image-wrapper, .pcard__media')) return 'product_image';
    return 'product_card';
  };

  const ctaElementIds = {
    'home.hero': 'shop_multichrome',
    'home.color_comparison': 'shop_the_shade',
    'home.build_your_own': 'start_building',
    'home.how_to_wear': 'shop_multichrome',
  };

  const trackModuleClick = (event, module) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const moduleId = module.dataset.obModuleId;
    const product = productFromElement(target, moduleId);
    if (product) {
      emit(isCollabs ? 'collabs_cta_click' : 'home_module_click', {
        ...eventBase(moduleId, 'product_card', productClickTarget(target)),
        items: [product],
      });
      return;
    }

    const collectionCard = target.closest('[data-ob-collection-card]');
    if (collectionCard) {
      emit('home_module_click', {
        ...eventBase(moduleId, 'collection_card', 'category_card'),
        collection_id: collectionCard.dataset.obCollectionId,
        collection_name: collectionCard.dataset.obCollectionTitle,
        collection_handle: collectionCard.dataset.obCollectionHandle,
        index: numberValue(collectionCard.dataset.obCollectionPosition),
      });
      return;
    }

    if (target.closest('[role="tab"], .tabs__tab, [data-tab]')) {
      emit('home_module_interaction', eventBase(moduleId, 'product_tab', 'tab_change'));
      return;
    }

    if (target.closest('.swiper-button-prev, .swiper-btn-prev, [name="previous"]')) {
      emit('home_module_interaction', eventBase(moduleId, 'carousel_previous', 'carousel_control'));
      return;
    }

    if (target.closest('.swiper-button-next, .swiper-btn-next, [name="next"]')) {
      emit('home_module_interaction', eventBase(moduleId, 'carousel_next', 'carousel_control'));
      return;
    }

    if (target.closest('video, .deferred-media__poster, [data-media-control]')) {
      emit('home_module_interaction', eventBase(moduleId, 'video', 'video_interaction'));
      return;
    }

    const control = target.closest('a[href], button');
    if (!control) return;

    let elementId = ctaElementIds[moduleId] || 'primary_cta';
    const href = control.getAttribute('href') || '';
    if (href === '#ourbella-collabs-application') elementId = 'apply_to_join';
    if (href === '#ourbella-creator-process') elementId = 'see_how_it_works';

    emit(isCollabs ? 'collabs_cta_click' : 'home_module_click', {
      ...eventBase(moduleId, elementId, 'cta'),
    });
  };

  const productItem = (variant) => {
    const product = { ...(config.product || {}) };
    if (variant) {
      product.item_variant = variant.title;
      product.variant_id = String(variant.id || '');
      product.price = numberValue(variant.price) !== undefined ? numberValue(variant.price) / 100 : product.price;
    }

    return cleanObject({
      item_id: String(product.item_id || ''),
      item_name: product.item_name,
      item_brand: product.item_brand,
      item_category: product.item_category,
      item_handle: product.item_handle,
      item_variant: product.item_variant,
      variant_id: String(product.variant_id || ''),
      price: product.price,
      currency: product.currency,
    });
  };

  const trackProductPage = () => {
    if (config.page_type !== 'product' || !config.product) return;

    emit('product_detail_view', {
      ...eventBase('product.detail', 'product_page', 'view'),
      currency: config.product.currency,
      value: config.product.price,
      items: [productItem()],
    });

    document.addEventListener('variant:changed', (event) => {
      const variant = event.detail?.variant;
      if (!variant) return;
      config.product.item_variant = variant.title;
      config.product.variant_id = String(variant.id || '');
      config.product.price = numberValue(variant.price) / 100;
      emit('product_variant_change', {
        ...eventBase('product.detail', 'variant_picker', 'variant_change'),
        currency: config.product.currency,
        value: numberValue(variant.price) / 100,
        items: [productItem(variant)],
      });
    });
  };

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const module = target.closest('[data-ob-module-id]');
    if (module) trackModuleClick(event, module);

    if (config.page_type !== 'product' || !config.product) return;

    if (target.closest('.shopify-payment-button__button, .shopify-payment-button button')) {
      emit('product_buy_now_click', {
        ...eventBase('product.detail', 'buy_now', 'buy_now'),
        currency: config.product.currency,
        value: config.product.price,
        items: [productItem()],
      });
      return;
    }

    const media = target.closest(
      '.product__thumbs-item, .product__media-item, .product__media-zoom, .product__media-gallery .swiper-button-prev, .product__media-gallery .swiper-button-next'
    );
    if (media) {
      emit('product_media_interaction', {
        ...eventBase('product.detail', 'media_gallery', 'media_interaction'),
        media_id: media.dataset.target || media.dataset.mediaId,
        media_position: media.dataset.mediaPosition || media.dataset.mediaIndex,
        items: [productItem()],
      });
    }
  });

  document.addEventListener('change', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const module = target?.closest('[data-ob-module-id]');
    if (!module || !target.matches('input[type="range"]')) return;
    emit('home_module_interaction', {
      ...eventBase(module.dataset.obModuleId, 'comparison_slider', 'comparison_drag'),
      interaction_value: target.value,
    });
  });

  startModuleObserver();
  trackProductPage();
})();
